/* Copyright (c) 2023 TORITI */

import Request from './request';

export default class MerchantService {
  static async getList(query = {}) {
    return new Promise((resolve) => {
      Request.send({
        method: 'GET',
        path: 'merchant',
        query
      }).then((result = {}) => {
        console.log('Merchant list response:', result);
        
        // Handle nested response structure if present
        if (result.success && result.data && Array.isArray(result.data)) {
          return resolve({
            success: true,
            data: result.data,
            pagination: result.pagination,
            message: result.message
          });
        }
        
        return resolve(result);
      }).catch((error) => {
        console.error('Merchant list error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to fetch merchants' }] });
      });
    });
  }

  static async getById(id) {
    return new Promise((resolve) => {
      Request.send({
        method: 'GET',
        path: `merchant/${id}`
      }).then((result = {}) => {
        console.log('Merchant detail response:', result);
        
        // Handle nested response structure: result.data.data contains the actual merchant
        if (result.success && result.data && result.data.data) {
          return resolve({
            success: true,
            data: result.data.data,
            message: result.data.message || result.message
          });
        }
        
        return resolve(result);
      }).catch((error) => {
        console.error('Merchant detail error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to fetch merchant' }] });
      });
    });
  }

  static async create(data = {}) {
    return new Promise((resolve) => {
      // Ensure we only send fields that the API expects
      const apiData = {
        businessName: data.businessName,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2 || '',
        city: data.city,
        state: data.state,
        status: data.status,
        postalCode: data.postalCode,
        phoneE164: data.phoneE164,
        websiteUrl: data.websiteUrl || '',
        googleReviewUrl: data.googleReviewUrl || '',
        yelpReviewUrl: data.yelpReviewUrl || '',
        timezone: data.timezone,
        defaultSmsQuota: data.defaultSmsQuota || 1000,
        planId: data.planId || 'starter',
      };

      if (data.industryCode) {
        apiData.industryCode = data.industryCode;
      }

      console.log('Creating merchant with data:', apiData);

      Request.send({
        method: 'POST',
        path: 'merchant',
        data: apiData
      }).then((result = {}) => {
        console.log('Merchant create response:', result);
        
        // Handle nested response structure if present
        if (result.success && result.data && result.data.data) {
          return resolve({
            success: true,
            data: result.data.data,
            message: result.data.message || result.message
          });
        }
        
        return resolve(result);
      }).catch((error) => {
        console.error('Merchant create error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to create merchant' }] });
      });
    });
  }

  static async update(id, data = {}) {
    return new Promise((resolve) => {
      Request.send({
        method: 'PUT',
        path: `merchant/${id}`,
        data
      }).then((result = {}) => {
        console.log('Merchant update response:', result);
        return resolve(result);
      }).catch((error) => {
        console.error('Merchant update error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to update merchant' }] });
      });
    });
  }

  static async delete(id) {
    return new Promise((resolve) => {
      console.log(`Deleting merchant with ID: ${id}`);
      
      Request.send({
        method: 'DELETE',
        path: `merchant/${id}`
      }).then((result = {}) => {
        console.log('Merchant delete response:', result);
        
        // Handle HTTP 204 No Content or success responses
        if (result.success || result.statusCode === 204) {
          return resolve({
            success: true,
            message: result.message || 'Merchant deleted successfully'
          });
        }
        
        return resolve(result);
      }).catch((error) => {
        console.error('Merchant delete error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to delete merchant' }] });
      });
    });
  }

  static async updatePreferences(merchantId, preferences = {}) {
    return new Promise((resolve) => {
      Request.send({
        method: 'PUT',
        path: `merchant-pref/${merchantId}`,
        data: preferences
      }).then((result = {}) => {
        console.log('Merchant preferences update response:', result);
        return resolve(result);
      }).catch((error) => {
        console.error('Merchant preferences update error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to update preferences' }] });
      });
    });
  }

  // ==================== SMS Quota Management ====================

  static async getQuotaSummary(merchantId) {
    return new Promise((resolve) => {
      Request.send({
        method: 'GET',
        path: `admin/merchants/${merchantId}/quota_summary`
      }).then((result = {}) => {
        console.log('Quota summary response:', result);
        return resolve(result);
      }).catch((error) => {
        console.error('Quota summary error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to load quota summary' }] });
      });
    });
  }

  static async updateCurrentQuota(merchantId, limitCount) {
    return new Promise((resolve) => {
      Request.send({
        method: 'PUT',
        path: `admin/merchants/${merchantId}/quota`,
        data: { limitCount }
      }).then((result = {}) => {
        console.log('Update current quota response:', result);
        return resolve(result);
      }).catch((error) => {
        console.error('Update current quota error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to update current quota' }] });
      });
    });
  }

  static async updateDefaultQuota(merchantId, defaultSmsQuota) {
    return new Promise((resolve) => {
      Request.send({
        method: 'PUT',
        path: `admin/merchants/${merchantId}/default_quota`,
        data: { defaultSmsQuota }
      }).then((result = {}) => {
        console.log('Update default quota response:', result);
        return resolve(result);
      }).catch((error) => {
        console.error('Update default quota error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to update default quota' }] });
      });
    });
  }

  // ==================== SMS Overage Settings ====================

  static async getSmsOverageSettings(merchantId) {
    return new Promise((resolve) => {
      Request.send({
        method: 'GET',
        path: `admin/merchants/${merchantId}/sms-settings`
      }).then((result = {}) => {
        console.log('SMS overage settings response:', result);
        return resolve(result);
      }).catch((error) => {
        console.error('SMS overage settings error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to load SMS overage settings' }] });
      });
    });
  }

  static async updateSmsOverageSettings(merchantId, allowSmsOverage) {
    return new Promise((resolve) => {
      Request.send({
        method: 'PATCH',
        path: `admin/merchants/${merchantId}/sms-settings`,
        data: { allowSmsOverage }
      }).then((result = {}) => {
        console.log('SMS overage update response:', result);
        return resolve(result);
      }).catch((error) => {
        console.error('SMS overage update error:', error);
        return resolve({ success: false, errors: [{ message: error.message || 'Failed to update SMS overage settings' }] });
      });
    });
  }
}
